import { ListedInstrument } from '@eih-netbroker/api-instrument';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { InstrumentResource } from '../../resources';
import { InstrumentTickSize, instrumentTickSizeAtom, InstrumentTickSizeMap } from '../../state';
import { getInstrumentIdForce, getUnknownListedInstrument } from '../../types';
import { GetResourceHeaderWithToken } from '../useResource';

export type UseInstrumentSearchTranslation = {
  unknown: string;
};

export type UseInstrumentSearchParams = {
  translation: UseInstrumentSearchTranslation;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  instrumentResource: InstrumentResource;
};

export type SearchInstrument = (
  query: string,
  instrumentCategory?: string,
  transactionId?: string,
) => Promise<Array<ListedInstrument>>;
export type BatchInstrumentSearch = (isins: Array<string>) => Promise<Array<ListedInstrument>>;
export type GetListedInstrument = (ric: string) => Promise<ListedInstrument | undefined>;
export type GetListedInstrumentList = (ricList: Array<string>) => Promise<Array<ListedInstrument>>;
export type GetTickSizeForInstrument = (instrument: ListedInstrument) => Promise<Array<InstrumentTickSize>>;
export type GetTickSizeForInstrumentAndPrice = (instrument: ListedInstrument, price: number) => Promise<number>;
export type GetListedInstrumentMap = (ricList: Array<string>) => Promise<Map<string, ListedInstrument> | undefined>;
export type GetInstrumentTickSizeOfList = (tickSizes: Array<InstrumentTickSize>, price: number) => number;

export type UseInstumentSearchCommon = (params: UseInstrumentSearchParams) => {
  searchInstrument: SearchInstrument;
  batchInstrumentSearch: BatchInstrumentSearch;
  getListedInstrument: GetListedInstrument;
  getListedInstrumentList: GetListedInstrumentList;
  getTickSizeForInstrument: GetTickSizeForInstrument;
  getTickSizeForInstrumentAndPrice: GetTickSizeForInstrumentAndPrice;
  getListedInstrumentMap: GetListedInstrumentMap;
  instrumentTickSize: InstrumentTickSizeMap;
  getInstrumentTickSizeOfList: GetInstrumentTickSizeOfList;
};

export const useInstrumentSearchCommon: UseInstumentSearchCommon = ({
  translation,
  getResourceHeaderWithToken,
  instrumentResource,
}: UseInstrumentSearchParams) => {
  const [instrumentTickSize, setInstrumentTickSize] = useAtom(instrumentTickSizeAtom);

  const searchInstrument = useCallback(
    async (query: string, instrumentCategory?: string, transactionId?: string): Promise<Array<ListedInstrument>> => {
      if (query.trim() === '') {
        return [];
      }
      const q = query.toUpperCase();

      const response = await instrumentResource.searchListedInstrument(
        { q, instrumentCategory },
        getResourceHeaderWithToken(transactionId),
      );

      return response.data;
    },
    [instrumentResource, getResourceHeaderWithToken],
  );

  const batchInstrumentSearch = useCallback(
    async (isins: Array<string>): Promise<Array<ListedInstrument>> => {
      if (isins.length === 0) {
        return [];
      }
      const response = await instrumentResource.batchInstrumentQuery({ isins }, getResourceHeaderWithToken());

      return response.data;
    },
    [instrumentResource, getResourceHeaderWithToken],
  );

  const defaultInstrument = useCallback(
    (ric: string): ListedInstrument => getUnknownListedInstrument(ric, translation.unknown),
    [translation],
  );

  const getListedInstrumentList = useCallback(
    async (ricList: Array<string>): Promise<Array<ListedInstrument>> => {
      if (ricList.length === 0) {
        return [];
      }
      const response = await instrumentResource.batchListedInstrumentQuery(
        {
          rics: ricList,
        },
        getResourceHeaderWithToken(),
      );

      if (response.data.length > 0) {
        return ricList.map(
          ric => response.data.find(listedInstrument => listedInstrument.ric === ric) || defaultInstrument(ric),
        );
      }

      return [];
    },
    [getResourceHeaderWithToken, instrumentResource, defaultInstrument],
  );

  const getListedInstrumentMap = useCallback(
    async (ricList: Array<string>): Promise<Map<string, ListedInstrument> | undefined> => {
      const list = await getListedInstrumentList(ricList);
      if (!list) {
        return undefined;
      }
      return new Map(list.map(instrument => [instrument.ric, instrument]));
    },
    [getListedInstrumentList],
  );

  const getListedInstrument = useCallback(
    async (ric: string): Promise<ListedInstrument | undefined> => {
      return (await getListedInstrumentList([ric]))?.[0];
    },
    [getListedInstrumentList],
  );

  const getInstrumentTickSizeOfList: GetInstrumentTickSizeOfList = useCallback(
    (tickSizes: Array<InstrumentTickSize>, price: number) => {
      const selectedTickSizeInterval = tickSizes.find(
        tickSize => Number(tickSize.lowerLimitPrice) <= price && price < Number(tickSize.upperLimitPrice),
      );
      if (!selectedTickSizeInterval) {
        throw new Error(`Not found valid tick size for price ${price}`);
      }
      return Number(selectedTickSizeInterval.tickSizeValue);
    },
    [],
  );

  const getTickSizeForInstrumentAndPrice = useCallback(
    async (instrument: ListedInstrument, price: number) => {
      const instrumentId = getInstrumentIdForce(instrument);
      const value = instrumentTickSize[instrumentId];
      if (!value) {
        const { data: instrumentTickSize } = await instrumentResource.getInstrumentTickSize(
          instrument,
          getResourceHeaderWithToken(),
        );

        setInstrumentTickSize(prev => ({ ...prev, [instrumentId]: instrumentTickSize }));
        return getInstrumentTickSizeOfList(instrumentTickSize, price);
      }
      return getInstrumentTickSizeOfList(value, price);
    },
    [
      instrumentResource,
      getInstrumentTickSizeOfList,
      instrumentTickSize,
      setInstrumentTickSize,
      getResourceHeaderWithToken,
    ],
  );

  const getTickSizeForInstrument = useCallback(
    async (instrument: ListedInstrument): Promise<Array<InstrumentTickSize>> => {
      const instrumentId = getInstrumentIdForce(instrument);
      const value = instrumentTickSize[instrumentId];
      if (!value) {
        const { data: instrumentTickSize } = await instrumentResource.getInstrumentTickSize(
          instrument,
          getResourceHeaderWithToken(),
        );

        setInstrumentTickSize(prev => ({ ...prev, [instrumentId]: instrumentTickSize }));
        return instrumentTickSize;
      }
      return value;
    },
    [
      instrumentResource,
      getInstrumentTickSizeOfList,
      instrumentTickSize,
      setInstrumentTickSize,
      getResourceHeaderWithToken,
    ],
  );

  return useMemo(
    () => ({
      instrumentTickSize,
      searchInstrument,
      batchInstrumentSearch,
      getListedInstrument,
      getListedInstrumentList,
      getTickSizeForInstrumentAndPrice,
      getListedInstrumentMap,
      getTickSizeForInstrument,
      getInstrumentTickSizeOfList,
    }),
    [
      instrumentTickSize,
      searchInstrument,
      batchInstrumentSearch,
      getListedInstrument,
      getListedInstrumentList,
      getTickSizeForInstrumentAndPrice,
      getListedInstrumentMap,
      getInstrumentTickSizeOfList,
    ],
  );
};
